import { Mark, mergeAttributes } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

export const ConditionalBlock = Mark.create({
    name: 'conditionalBlock',
    addAttributes() {
        return {
            ruleId: {
                default: '',
                parseHTML: (element) => element.getAttribute('data-rule-id'),
                renderHTML: (attributes) => ({ 'data-rule-id': attributes.ruleId }),
            },
            operator: {
                default: '&&',
                parseHTML: (element) => {
                    const op = element.getAttribute('data-operator') || '&&';
                    return op === 'OR' || op === '||' ? '||' : '&&';
                },
                renderHTML: (attributes) => ({ 'data-operator': attributes.operator || '&&' }),
            },
            rules: {
                default: [],
                parseHTML: (element) => {
                    let val = element.getAttribute('data-rules') || element.getAttribute('data-conditions');
                    if (!val) return [];
                    try {
                        if (typeof val === 'string') {
                            if (val.startsWith('%5B') || val.startsWith('%7B')) {
                                val = decodeURIComponent(val);
                            }
                            if (val.includes('&quot;') || val.includes('&#123;') || val.includes('&#125;') || val.includes('&amp;')) {
                                val = val
                                    .replace(/&quot;/g, '"')
                                    .replace(/&#123;/g, '{')
                                    .replace(/&#125;/g, '}')
                                    .replace(/&amp;/g, '&');
                            }
                            return JSON.parse(val);
                        }
                        return Array.isArray(val) ? val : [];
                    }
                    catch {
                        return [];
                    }
                },
                renderHTML: (attributes) => ({
                    'data-rules': JSON.stringify(attributes.rules || []),
                }),
            },
            label: {
                default: '',
                parseHTML: (element) => element.getAttribute('data-rule-label'),
                renderHTML: (attributes) => ({
                    'data-rule-label': attributes.label, ...(attributes.label ? { title: `Condition: ${attributes.label}` } : {}),
                }),
            },
        };
    },
    parseHTML() {
        return [{ tag: 'span[data-conditional-block]' }];
    },
    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            mergeAttributes({ 'data-conditional-block': '', class: 'conditional-mark' }, HTMLAttributes),
            0,
        ];
    },
    addProseMirrorPlugins() {
        const pluginKey = new PluginKey('conditionalBlockBadge');
        return [
            new Plugin({
                key: pluginKey,
                props: {
                    decorations(state) {
                        const { doc } = state;
                        const decorations = [];
                        let currentRange = null;

                        doc.descendants((node, pos) => {
                            if (node.type.name === 'table') {
                                if (currentRange) {
                                    decorations.push(createBadgeDecoration(currentRange));
                                    currentRange = null;
                                }
                                const tableCond = node.attrs?.tableCondition;
                                if (tableCond && !isInvalidOrAlignmentCondition(tableCond)) {
                                    let hasConditionBefore = false;
                                    try {
                                        const $pos = doc.resolve(pos);
                                        if ($pos.nodeBefore) {
                                            const prevText = ($pos.nodeBefore.textContent || '').toLowerCase();
                                            const cleanKey = cleanConditionDisplay(tableCond, { rules: node.attrs?.tableConditionData }).toLowerCase();
                                            if (cleanKey && (prevText.includes(cleanKey) || prevText.includes('condition:') || prevText.includes(cleanKey.replace(/_/g, ' ')))) {
                                                hasConditionBefore = true;
                                            }
                                        }
                                    } catch (e) {}
                                    if (!hasConditionBefore) {
                                        decorations.push(createTableBadgeDecoration(node, pos));
                                    }
                                }

                                // 1. Table-level Column Condition Badges
                                const colConditions = node.attrs?.columnConditions;
                                if (colConditions && typeof colConditions === 'object') {
                                    const firstRow = node.maybeChild(0);
                                    if (firstRow) {
                                        let cellOffset = pos + 2; // pos + 1 (first row) + 1 (first cell)
                                        let cIdx = 0;
                                        firstRow.forEach((cellChild) => {
                                            const colRule = colConditions[cIdx];
                                            if (colRule) {
                                                let badgePos = cellOffset + 2;
                                                cellChild.descendants((child, childPos) => {
                                                    if (child.isInline) {
                                                        badgePos = cellOffset + 1 + childPos;
                                                        return false;
                                                    }
                                                });
                                                decorations.push(createColBadgeDecoration(node, pos, cIdx, badgePos, colRule));
                                            }
                                            cellOffset += cellChild.nodeSize;
                                            cIdx++;
                                        });
                                    }
                                }

                                // 2. Entire Row Condition Badges (Left top corner of row)
                                let rowOffset = pos + 1;
                                let rIdx = 0;
                                node.forEach((rowChild) => {
                                    const rowCond = rowChild.attrs?.rowCondition;
                                    if (rowCond && !isInvalidOrAlignmentCondition(rowCond)) {
                                        // Always place badge in cell 0 (the left top corner of the row)
                                        const firstCell = rowChild.maybeChild(0);
                                        const firstCellPos = rowOffset + 1;
                                        let badgePos = firstCellPos + 2;
                                        if (firstCell) {
                                            firstCell.descendants((child, childPos) => {
                                                if (child.isInline) {
                                                    badgePos = firstCellPos + 1 + childPos;
                                                    return false;
                                                }
                                            });
                                        }

                                        decorations.push(createRowBadgeDecoration(rowChild, rowOffset, badgePos, rIdx, rowCond, rowChild.attrs?.rowConditionData, pos));
                                        decorations.push(Decoration.node(rowOffset, rowOffset + rowChild.nodeSize, {
                                            class: 'table-row-conditional',
                                        }));
                                    }
                                    rowOffset += rowChild.nodeSize;
                                    rIdx++;
                                });

                                return true;
                            }

                            if (!node.isInline) {
                                if (currentRange) {
                                    decorations.push(createBadgeDecoration(currentRange));
                                    currentRange = null;
                                }
                                return true;
                            }

                            const mark = node.marks?.find((m) => m.type.name === 'conditionalBlock');
                            const ruleId = mark?.attrs?.ruleId || (mark ? JSON.stringify(mark.attrs) : null);

                            if (mark && ruleId) {
                                if (currentRange && currentRange.ruleId === ruleId) {
                                    currentRange.to = pos + node.nodeSize;
                                } else {
                                    if (currentRange) {
                                        decorations.push(createBadgeDecoration(currentRange));
                                    }
                                    currentRange = {
                                        from: pos,
                                        to: pos + node.nodeSize,
                                        ruleId,
                                        attrs: mark.attrs,
                                    };
                                }
                            } else {
                                if (currentRange) {
                                    decorations.push(createBadgeDecoration(currentRange));
                                    currentRange = null;
                                }
                            }
                            return false;
                        });

                        if (currentRange) {
                            decorations.push(createBadgeDecoration(currentRange));
                        }

                        return DecorationSet.create(doc, decorations);
                    },
                },
            }),
        ];
    },
});

export function cleanConditionDisplay(rawText, attrs) {
    let text = rawText || attrs?.label || '';
    text = text.replace(/<\/?(span|strong|b|i|em|p|div|font|u|small|a)\b[^>]*>/gi, '');
    text = text.replace(/\*\*/g, '').trim();
    text = text.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&amp;/g, '&');
    
    // Check if corrupted like "IF >", ">", "<", or purely whitespace/symbols
    const isCorrupted = !text || /^((ROW\s+)?(COL\s+)?IF\s*)?[><=\s\-_]+$/i.test(text);
    if (isCorrupted) {
        let rules = attrs?.rules;
        if ((!rules || rules.length === 0) && attrs?.rowConditionData) {
            try {
                const parsed = typeof attrs.rowConditionData === 'string' ? JSON.parse(attrs.rowConditionData) : attrs.rowConditionData;
                rules = parsed.rules;
            } catch (e) {}
        }
        if ((!rules || rules.length === 0) && attrs?.colRule) {
            try {
                const parsed = typeof attrs.colRule === 'string' ? JSON.parse(attrs.colRule) : attrs.colRule;
                rules = parsed.rules;
            } catch (e) {}
        }
        if (Array.isArray(rules) && rules.length > 0) {
            const parts = rules.map(r => {
                const f = (r.variable || r.field || '').replace(/<\/?(span|strong|b|i|em|p|div|font|u|small|a)\b[^>]*>/gi, '').replace(/<<+|>>+/g, '').trim();
                const op = (r.operator || 'exists').toLowerCase();
                const v = r.value !== undefined && r.value !== null ? String(r.value).replace(/<\/?(span|strong|b|i|em|p|div|font|u|small|a)\b[^>]*>/gi, '').replace(/<<+|>>+/g, '').trim() : '';
                if (op === 'exists' || op === 'is_not_empty') {
                    return f;
                }
                return v ? `${f} == ${v}` : f;
            }).filter(Boolean);
            text = parts.join((attrs?.operator === '||' || attrs?.operator === 'OR') ? ' || ' : ' && ');
        }
    }

    text = text.replace(/^Row Condition:\s*/i, '').replace(/^Condition:\s*/i, '').replace(/^(ROW\s+)?(COL\s+)?IF\s+/i, '').trim();
    text = text.replace(/\s+EXISTS\b/gi, '').replace(/\s+exists\b/gi, '').trim();
    text = text.replace(/^[\[\{]+/, '').replace(/[\]\}]+$/, '').trim();
    text = text.replace(/<<+/g, '').replace(/>>+/g, '').trim();

    return text || 'Condition';
}

export function isInvalidOrAlignmentCondition(condStr) {
    if (!condStr) return true;
    const str = String(condStr).trim();
    if (!str) return true;
    if (/[a-zA-Z0-9]/.test(str)) {
        return false;
    }
    const lower = str.toLowerCase();
    if (['>', '<', '=>', '<=', '==', '=', '=&gt;', '&lt;=', '&gt;', '&lt;', '[>]', '[<]', '[=>]', '[<=]', '[==]'].includes(lower)) {
        return true;
    }
    if (/^[\s><=\[\]\{\}|&.,;:\-_]+$/.test(str)) {
        return true;
    }
    return false;
}

function createBadgeDecoration(range) {
    return Decoration.widget(
        range.from,
        (view) => {
            const badge = document.createElement('span');
            badge.className = 'conditional-badge-widget';
            badge.setAttribute('contenteditable', 'false');

            const cleanLabel = cleanConditionDisplay(range.attrs?.label, range.attrs);

            const contentWrapper = document.createElement('span');
            contentWrapper.className = 'conditional-badge-content';

            const headerSpan = document.createElement('span');
            headerSpan.className = 'conditional-badge-header';
            headerSpan.textContent = 'Condition:';

            const textSpan = document.createElement('span');
            textSpan.className = 'conditional-badge-label';
            textSpan.textContent = cleanLabel;
            textSpan.title = `Condition: ${cleanLabel} (Click to edit)`;

            contentWrapper.appendChild(headerSpan);
            contentWrapper.appendChild(textSpan);

            const handleEdit = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const event = new CustomEvent('openConditionEdit', {
                    detail: {
                        ruleId: range.attrs?.ruleId,
                        data: {
                            operator: range.attrs?.operator || 'AND',
                            rules: range.attrs?.rules || [],
                            label: range.attrs?.label || cleanLabel,
                        },
                    },
                });
                window.dispatchEvent(event);
            };

            contentWrapper.onclick = handleEdit;

            const actions = document.createElement('span');
            actions.className = 'conditional-badge-actions';

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'conditional-badge-btn conditional-badge-edit-btn';
            editBtn.title = 'Edit condition';
            editBtn.setAttribute('aria-label', 'Edit condition');
            editBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>';
            editBtn.onclick = handleEdit;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'conditional-badge-btn conditional-badge-remove-btn';
            removeBtn.title = 'Remove condition';
            removeBtn.setAttribute('aria-label', 'Remove condition');
            removeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
            removeBtn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const state = view.state;
                const markType = state.schema.marks.conditionalBlock;
                if (markType) {
                    const tr = state.tr;
                    state.doc.descendants((node, pos) => {
                        if (node.isInline) {
                            const mark = node.marks?.find(m => m.type === markType && (m.attrs?.ruleId === range.attrs?.ruleId || (range.attrs?.ruleId && m.attrs?.ruleId === range.ruleId)));
                            if (mark) {
                                tr.removeMark(pos, pos + node.nodeSize, markType);
                            }
                        }
                    });
                    tr.removeMark(range.from, range.to, markType);
                    view.dispatch(tr);
                }
            };

            actions.appendChild(editBtn);
            actions.appendChild(removeBtn);

            badge.appendChild(contentWrapper);
            badge.appendChild(actions);
            return badge;
        },
        { side: -1, stopEvent: () => false, ignoreSelection: true }
    );
}

function createTableBadgeDecoration(node, pos) {
    return Decoration.widget(
        pos,
        (view) => {
            let rawLabel = node.attrs?.tableCondition || '';
            if (isInvalidOrAlignmentCondition(rawLabel)) {
                return document.createElement('span');
            }

            const wrapper = document.createElement('div');
            wrapper.className = 'table-conditional-badge-wrapper';
            wrapper.setAttribute('contenteditable', 'false');

            const badge = document.createElement('span');
            badge.className = 'conditional-badge-widget table-conditional-badge';
            badge.setAttribute('contenteditable', 'false');

            const cleanLabel = cleanConditionDisplay(rawLabel, { rules: node.attrs?.tableConditionData });

            const contentWrapper = document.createElement('span');
            contentWrapper.className = 'conditional-badge-content';

            const headerSpan = document.createElement('span');
            headerSpan.className = 'conditional-badge-header';
            headerSpan.textContent = 'Table Condition:';

            const textSpan = document.createElement('span');
            textSpan.className = 'conditional-badge-label';
            textSpan.textContent = cleanLabel;
            textSpan.title = `Table Condition: ${cleanLabel} (Click to edit)`;

            contentWrapper.appendChild(headerSpan);
            contentWrapper.appendChild(textSpan);

            const handleEdit = (e) => {
                e.preventDefault();
                e.stopPropagation();

                let rules = [];
                let operator = '&&';
                if (node.attrs?.tableConditionData) {
                    try {
                        const parsed = typeof node.attrs.tableConditionData === 'string' 
                            ? JSON.parse(node.attrs.tableConditionData) 
                            : node.attrs.tableConditionData;
                        rules = parsed.rules || [];
                        operator = parsed.operator || '&&';
                    } catch (err) {}
                }
                if (!rules || rules.length === 0) {
                    const cleanRaw = cleanLabel;
                    const eqMatch = cleanRaw.match(/<<([^>]+)>>\s*==\s*([^\s&|]+)/);
                    const existsMatch = cleanRaw.match(/<<([^>]+)>>/);
                    if (eqMatch) {
                        rules = [{ field: eqMatch[1].trim(), variable: eqMatch[1].trim(), operator: 'equals', value: eqMatch[2].trim() }];
                    } else if (existsMatch) {
                        rules = [{ field: existsMatch[1].trim(), variable: existsMatch[1].trim(), operator: 'exists', value: '' }];
                    } else if (cleanRaw) {
                        rules = [{ field: cleanRaw, variable: cleanRaw, operator: 'exists', value: '' }];
                    }
                }

                const event = new CustomEvent('openConditionEdit', {
                    detail: {
                        tablePos: pos,
                        ruleId: `table_${pos}`,
                        data: {
                            operator: operator || 'AND',
                            rules,
                            label: rawLabel || cleanLabel,
                        },
                    },
                });
                window.dispatchEvent(event);
            };

            contentWrapper.onclick = handleEdit;

            const actions = document.createElement('span');
            actions.className = 'conditional-badge-actions';

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'conditional-badge-btn conditional-badge-edit-btn';
            editBtn.title = 'Edit table condition';
            editBtn.setAttribute('aria-label', 'Edit table condition');
            editBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>';
            editBtn.onclick = handleEdit;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'conditional-badge-btn conditional-badge-remove-btn';
            removeBtn.title = 'Remove table condition';
            removeBtn.setAttribute('aria-label', 'Remove table condition');
            removeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
            removeBtn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const state = view.state;
                let targetPos = null;
                let targetNode = null;
                state.doc.descendants((n, p) => {
                    if (n.type.name === 'table') {
                        if (p === pos || (pos >= p && pos < p + n.nodeSize)) {
                            targetPos = p;
                            targetNode = n;
                            return false;
                        }
                    }
                });
                if (targetNode && targetPos !== null) {
                    const tr = state.tr;
                    const newAttrs = { ...targetNode.attrs };
                    newAttrs.tableCondition = null;
                    newAttrs.tableConditionData = null;
                    delete newAttrs.tableCondition;
                    delete newAttrs.tableConditionData;
                    tr.setNodeMarkup(targetPos, null, newAttrs);
                    view.dispatch(tr);
                }
            };

            actions.appendChild(editBtn);
            actions.appendChild(removeBtn);

            badge.appendChild(contentWrapper);
            badge.appendChild(actions);
            wrapper.appendChild(badge);
            return wrapper;
        },
        { side: -1, stopEvent: () => false, ignoreSelection: true }
    );
}

function createRowBadgeDecoration(rowNode, rowPos, badgePos, rowIndex, rawCond, rawCondData, tablePos) {
    return Decoration.widget(
        badgePos,
        (view) => {
            const wrapper = document.createElement('div');
            wrapper.className = 'table-row-conditional-badge-wrapper';
            wrapper.setAttribute('contenteditable', 'false');

            const badge = document.createElement('span');
            badge.className = 'conditional-badge-widget table-row-conditional-badge';
            badge.setAttribute('contenteditable', 'false');

            const cleanLabel = cleanConditionDisplay(rawCond, { rowConditionData: rawCondData });

            const contentWrapper = document.createElement('span');
            contentWrapper.className = 'conditional-badge-content';

            const headerSpan = document.createElement('span');
            headerSpan.className = 'conditional-badge-header';
            headerSpan.textContent = 'Condition:';

            const textSpan = document.createElement('span');
            textSpan.className = 'conditional-badge-label';
            textSpan.textContent = cleanLabel;
            textSpan.title = `Row Condition: ${cleanLabel} (Click to edit)`;

            contentWrapper.appendChild(headerSpan);
            contentWrapper.appendChild(textSpan);

            const handleEdit = (e) => {
                e.preventDefault();
                e.stopPropagation();

                let rules = [];
                let operator = '&&';
                if (rawCondData) {
                    try {
                        const parsed = typeof rawCondData === 'string' ? JSON.parse(rawCondData) : rawCondData;
                        rules = parsed.rules || [];
                        operator = parsed.operator || '&&';
                    } catch (err) {}
                }
                if (!rules || rules.length === 0) {
                    const cleanRaw = cleanLabel;
                    const eqMatch = cleanRaw.match(/<<([^>]+)>>\s*==\s*([^\s&|]+)/) || cleanRaw.match(/([^=]+)\s*==\s*(.+)/);
                    if (eqMatch) {
                        const f = eqMatch[1].trim();
                        rules = [{ field: f, variable: f, operator: 'equals', value: eqMatch[2].trim() }];
                    } else if (cleanRaw) {
                        rules = [{ field: cleanRaw, variable: cleanRaw, operator: 'exists', value: '' }];
                    }
                }

                const event = new CustomEvent('openConditionEdit', {
                    detail: {
                        tablePos,
                        rowPos,
                        rowIndex,
                        conditionType: 'row',
                        ruleId: `row_${rowPos}`,
                        data: {
                            operator: operator || 'AND',
                            rules,
                            label: rawCond,
                            targetScope: 'row',
                        },
                    },
                });
                window.dispatchEvent(event);
            };

            contentWrapper.onclick = handleEdit;

            const actions = document.createElement('span');
            actions.className = 'conditional-badge-actions';

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'conditional-badge-btn conditional-badge-edit-btn';
            editBtn.title = 'Edit row condition';
            editBtn.setAttribute('aria-label', 'Edit row condition');
            editBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>';
            editBtn.onclick = handleEdit;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'conditional-badge-btn conditional-badge-remove-btn';
            removeBtn.title = 'Remove row condition';
            removeBtn.setAttribute('aria-label', 'Remove row condition');
            removeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
            removeBtn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const state = view.state;
                let targetRowPos = null;
                let targetRow = null;
                state.doc.descendants((n, p) => {
                    if (n.type.name === 'tableRow') {
                        if (p === rowPos || (rowPos >= p && rowPos < p + n.nodeSize)) {
                            targetRowPos = p;
                            targetRow = n;
                            return false;
                        }
                    }
                });
                if (targetRow && targetRowPos !== null) {
                    const tr = state.tr;
                    const newAttrs = { ...targetRow.attrs };
                    newAttrs.rowCondition = null;
                    newAttrs.rowConditionData = null;
                    delete newAttrs.rowCondition;
                    delete newAttrs.rowConditionData;
                    tr.setNodeMarkup(targetRowPos, null, newAttrs);
                    view.dispatch(tr);
                }
            };

            actions.appendChild(editBtn);
            actions.appendChild(removeBtn);

            badge.appendChild(contentWrapper);
            badge.appendChild(actions);
            wrapper.appendChild(badge);
            return wrapper;
        },
        { side: -1, stopEvent: () => false, ignoreSelection: true }
    );
}

function createColBadgeDecoration(tableNode, tablePos, colIndex, badgePos, colRule) {
    return Decoration.widget(
        badgePos,
        (view) => {
            const wrapper = document.createElement('div');
            wrapper.className = 'table-col-conditional-badge-wrapper';
            wrapper.setAttribute('contenteditable', 'false');

            const badge = document.createElement('span');
            badge.className = 'conditional-badge-widget table-col-conditional-badge';
            badge.setAttribute('contenteditable', 'false');

            const rawCond = colRule?.label || (typeof colRule === 'string' ? colRule : '');
            const cleanLabel = cleanConditionDisplay(rawCond, { colRule, rules: colRule?.rules });

            const contentWrapper = document.createElement('span');
            contentWrapper.className = 'conditional-badge-content';

            const headerSpan = document.createElement('span');
            headerSpan.className = 'conditional-badge-header';
            headerSpan.textContent = 'Condition:';

            const textSpan = document.createElement('span');
            textSpan.className = 'conditional-badge-label';
            textSpan.textContent = cleanLabel;
            textSpan.title = `Column Condition: ${cleanLabel} (Click to edit)`;

            contentWrapper.appendChild(headerSpan);
            contentWrapper.appendChild(textSpan);

            const handleEdit = (e) => {
                e.preventDefault();
                e.stopPropagation();

                const rules = colRule?.rules || [];
                const operator = colRule?.operator || '&&';

                const event = new CustomEvent('openConditionEdit', {
                    detail: {
                        tablePos,
                        colIndex,
                        conditionType: 'column',
                        ruleId: `col_${colIndex}`,
                        data: {
                            operator: operator || 'AND',
                            rules,
                            label: rawCond,
                            targetScope: 'column',
                        },
                    },
                });
                window.dispatchEvent(event);
            };

            contentWrapper.onclick = handleEdit;

            const actions = document.createElement('span');
            actions.className = 'conditional-badge-actions';

            const editBtn = document.createElement('button');
            editBtn.type = 'button';
            editBtn.className = 'conditional-badge-btn conditional-badge-edit-btn';
            editBtn.title = 'Edit column condition';
            editBtn.setAttribute('aria-label', 'Edit column condition');
            editBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"><path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path></svg>';
            editBtn.onclick = handleEdit;

            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.className = 'conditional-badge-btn conditional-badge-remove-btn';
            removeBtn.title = 'Remove column condition';
            removeBtn.setAttribute('aria-label', 'Remove column condition');
            removeBtn.innerHTML = '<svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>';
            removeBtn.onclick = (e) => {
                e.preventDefault();
                e.stopPropagation();
                const state = view.state;
                let targetTablePos = null;
                let targetTable = null;
                state.doc.descendants((n, p) => {
                    if (n.type.name === 'table') {
                        if (p === tablePos || (tablePos >= p && tablePos < p + n.nodeSize)) {
                            targetTablePos = p;
                            targetTable = n;
                            return false;
                        }
                    }
                });
                if (targetTable && targetTablePos !== null) {
                    const tr = state.tr;
                    const existingCols = { ...(targetTable.attrs?.columnConditions || {}) };
                    delete existingCols[colIndex];
                    tr.setNodeMarkup(targetTablePos, null, {
                        ...targetTable.attrs,
                        columnConditions: Object.keys(existingCols).length > 0 ? existingCols : null,
                    });

                    // Remove conditional marks from data cells in that column
                    const markType = state.schema.marks.conditionalBlock;
                    if (markType) {
                        targetTable.forEach((rowChild, rOffset) => {
                            if (rowChild.childCount > colIndex) {
                                let currentC = 0;
                                rowChild.forEach((cellChild, cOffset) => {
                                    if (currentC === colIndex) {
                                        const cellAbsolutePos = targetTablePos + 1 + rOffset + 1 + cOffset;
                                        cellChild.descendants((inlineNode, inlineOffset) => {
                                            if (inlineNode.isInline) {
                                                const inlineAbsPos = cellAbsolutePos + 1 + inlineOffset;
                                                tr.removeMark(inlineAbsPos, inlineAbsPos + inlineNode.nodeSize, markType);
                                            }
                                        });
                                    }
                                    currentC++;
                                });
                            }
                        });
                    }
                    view.dispatch(tr);
                }
            };

            actions.appendChild(editBtn);
            actions.appendChild(removeBtn);

            badge.appendChild(contentWrapper);
            badge.appendChild(actions);
            wrapper.appendChild(badge);
            return wrapper;
        },
        { side: -1, stopEvent: () => false, ignoreSelection: true }
    );
}
